import { Suspense } from "react";
import { createBrowserRouter, Navigate, Outlet, RouterProvider } from "react-router-dom";
import { AppErrorBoundary, RouteErrorPage } from "@/components/AppErrorBoundary.js";
import { PageLoadingFallback } from "@/components/PageLoadingFallback.js";
import {
  AcceptInvitePage,
  AgentConfigPage,
  ApiKeysPage,
  AuthenticatedShell,
  CampaignContactsPage,
  CampaignListPage,
  CampaignWizardLayout,
  ContactSetDetailPage,
  ContactSetListPage,
  GenerateSamplePage,
  ForgotPasswordPage,
  LoginPage,
  ResetPasswordPage,
  SignupPage,
  MembersPage,
  NumberingAdminPage,
  OrganizationPage,
  ProfilePage,
  QuotaEditorPage,
  RunDetailPage,
  RunListPage,
  RunResultsPage,
  SettingsLayout,
  SurveyBuilderPage,
} from "@/routes/lazy-pages.js";

/** Wraps a lazily loaded page so route-level code splitting still shows a loading fallback. */
function suspensePage(page: React.ReactNode): React.JSX.Element {
  return <Suspense fallback={<PageLoadingFallback />}>{page}</Suspense>;
}

/**
 * Pathless parent so every top-level route inherits the same chunk-load / render error UI.
 * React Router catches those errors here; a parent React error boundary around the router does not.
 */
const router = createBrowserRouter([
  {
    element: <Outlet />,
    errorElement: <RouteErrorPage />,
    children: [
      {
        path: "/login",
        element: suspensePage(<LoginPage />),
      },
      {
        path: "/signup",
        element: suspensePage(<SignupPage />),
      },
      {
        path: "/forgot-password",
        element: suspensePage(<ForgotPasswordPage />),
      },
      {
        path: "/reset-password",
        element: suspensePage(<ResetPasswordPage />),
      },
      {
        path: "/accept-invite/:invitationId",
        element: suspensePage(<AcceptInvitePage />),
      },
      {
        element: suspensePage(<AuthenticatedShell />),
        children: [
          { index: true, element: <Navigate to="/campaigns" replace /> },
          { path: "campaigns", element: suspensePage(<CampaignListPage />) },
          {
            path: "campaigns/:campaignId",
            element: suspensePage(<CampaignWizardLayout />),
            children: [
              { index: true, element: <Navigate to="survey" replace /> },
              { path: "survey", element: suspensePage(<SurveyBuilderPage />) },
              { path: "contacts", element: suspensePage(<CampaignContactsPage />) },
              { path: "quota", element: suspensePage(<QuotaEditorPage />) },
              { path: "agent", element: suspensePage(<AgentConfigPage />) },
            ],
          },
          { path: "campaigns/:campaignId/runs", element: suspensePage(<RunListPage />) },
          { path: "campaigns/:campaignId/runs/:runId", element: suspensePage(<RunDetailPage />) },
          {
            path: "campaigns/:campaignId/runs/:runId/results",
            element: suspensePage(<RunResultsPage />),
          },
          { path: "contact-sets", element: suspensePage(<ContactSetListPage />) },
          {
            path: "contact-sets/generate",
            element: suspensePage(<GenerateSamplePage />),
          },
          {
            path: "contact-sets/generate/:market",
            element: suspensePage(<GenerateSamplePage />),
          },
          { path: "contact-sets/:contactSetId", element: suspensePage(<ContactSetDetailPage />) },
          {
            path: "settings",
            element: suspensePage(<SettingsLayout />),
            children: [
              { index: true, element: <Navigate to="profile" replace /> },
              { path: "profile", element: suspensePage(<ProfilePage />) },
              { path: "organization", element: suspensePage(<OrganizationPage />) },
              { path: "members", element: suspensePage(<MembersPage />) },
              { path: "api-keys", element: suspensePage(<ApiKeysPage />) },
              { path: "numbering", element: suspensePage(<NumberingAdminPage />) },
            ],
          },
        ],
      },
      { path: "*", element: <Navigate to="/campaigns" replace /> },
    ],
  },
]);

export function App(): React.JSX.Element {
  return (
    <AppErrorBoundary>
      <RouterProvider router={router} />
    </AppErrorBoundary>
  );
}
